import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LifeBuoy, Plus, Filter, X, Trash2, MessageSquare, Send, Paperclip, ImagePlus, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SUPPORT_ROLES = ["admin", "gestionnaire", "operateur", "technicien"];

const STATUS = [
  { v: "ouvert", l: "Ouvert", c: "#F5821F" },
  { v: "en_cours", l: "En cours", c: "#17A2B8" },
  { v: "en_attente", l: "En attente", c: "#A855F7" },
  { v: "resolu", l: "Résolu", c: "#10B981" },
  { v: "ferme", l: "Fermé", c: "#6b7280" },
];
const PRIORITY = [
  { v: "basse", l: "Basse", c: "#6b7280" },
  { v: "normale", l: "Normale", c: "#17A2B8" },
  { v: "haute", l: "Haute", c: "#F5821F" },
  { v: "urgente", l: "Urgente", c: "#EF4444" },
];
const CATEGORY = [
  { v: "support", l: "Support" },
  { v: "installation", l: "Installation" },
  { v: "facturation", l: "Facturation" },
  { v: "materiel", l: "Matériel" },
  { v: "autre", l: "Autre" },
];
const meta = (arr, v) => arr.find((x) => x.v === v) || { l: v, c: "#6b7280" };
const inp = "bg-secondary/40 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8]";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Pacific/Noumea", day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const toPayload = (t) => ({ subject: t.subject, description: t.description || "", client_name: t.client_name || "", company_id: t.company_id || null, vehicle_id: t.vehicle_id || null, category: t.category || "support", priority: t.priority || "normale", status: t.status || "ouvert", assignee: t.assignee || "", client_email: t.client_email || "", attachments: t.attachments || [] });
const fileUrl = (a) => (a.url?.startsWith("http") ? a.url : `${BACKEND_URL}${a.url}`);

const Badge = ({ item }) => (
  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border" style={{ color: item.c, background: `${item.c}1f`, borderColor: `${item.c}55` }}>{item.l}</span>
);

function Attachments({ list }) {
  if (!list || list.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid="ticket-attachments">
      {list.map((a, i) => {
        const isImg = /\.(png|jpe?g|webp|gif)$/i.test(a.name || a.path || "");
        return (
          <a key={i} href={fileUrl(a)} target="_blank" rel="noreferrer" data-testid={`ticket-attachment-${i}`} className="block border border-border rounded-md overflow-hidden hover:border-[#17A2B8] transition-colors" title={a.name}>
            {isImg ? (
              <img src={fileUrl(a)} alt={a.name} className="h-20 w-28 object-cover" />
            ) : (
              <span className="flex items-center gap-1 px-3 py-2 text-xs"><Paperclip className="w-3.5 h-3.5" /> {a.name}</span>
            )}
          </a>
        );
      })}
    </div>
  );
}

export default function TicketsPage() {
  const { user } = useAuth();
  const isSupport = SUPPORT_ROLES.includes(user?.role);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [fStatus, setFStatus] = useState("");
  const [fPriority, setFPriority] = useState("");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (fStatus) p.set("status", fStatus);
    if (fPriority) p.set("priority", fPriority);
    const [l, s] = await Promise.all([api.get(`/tickets?${p}`), api.get("/tickets/stats")]);
    setRows(l.data); setStats(s.data);
  }, [fStatus, fPriority]);
  useEffect(() => {
    if (isSupport) { api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, [isSupport]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (t, status) => { await api.put(`/tickets/${t.id}`, { ...toPayload(t), status }); load(); };
  const del = async (id) => { if (!window.confirm("Supprimer ce ticket ?")) return; await api.delete(`/tickets/${id}`); setDetail(null); load(); };

  const visible = rows.filter((t) => !q || (t.subject + t.client_name + (t.ref || "") + (t.company_name || "")).toLowerCase().includes(q.toLowerCase()));

  // ---------- Vue CLIENT : « Créer un ticket au support Boomerang » ----------
  if (!isSupport) {
    return (
      <Layout>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-extrabold flex items-center gap-2"><LifeBuoy className="w-6 h-6 brand-teal" /> Support Boomerang</h1>
            <p className="text-sm text-muted-foreground">Créez une demande d'assistance et suivez les réponses de notre équipe.</p>
          </div>
          <button onClick={() => setCreating(true)} data-testid="ticket-add-btn" className="flex items-center gap-1.5 bg-[#F5821F] hover:bg-[#e0741a] text-white font-semibold px-4 py-2.5 rounded-md text-sm"><Plus className="w-4 h-4" /> Créer un ticket au support</button>
        </div>

        <div className="grid gap-3">
          {visible.length === 0 && (
            <div className="glass rounded-xl p-10 text-center" data-testid="ticket-empty">
              <LifeBuoy className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="font-semibold">Aucune demande pour le moment</p>
              <p className="text-sm text-muted-foreground mt-1">Un souci, une question ? Créez un ticket, notre support vous répond rapidement.</p>
              <button onClick={() => setCreating(true)} data-testid="ticket-add-empty" className="mt-4 inline-flex items-center gap-1.5 bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold px-4 py-2 rounded-md text-sm"><Plus className="w-4 h-4" /> Créer un ticket</button>
            </div>
          )}
          {visible.map((t) => (
            <button key={t.id} onClick={() => setDetail(t)} data-testid={`ticket-row-${t.id}`} className="glass rounded-xl p-4 text-left hover:border-[#17A2B8] border border-transparent transition-colors">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] text-muted-foreground">{t.ref}</span>
                    <Badge item={meta(PRIORITY, t.priority)} />
                    <Badge item={meta(STATUS, t.status)} />
                    {(t.attachments || []).length > 0 && <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground"><Paperclip className="w-3 h-3" /> {t.attachments.length}</span>}
                    {(t.comments || []).length > 0 && <span className="inline-flex items-center gap-0.5 text-[11px] text-[#17A2B8]"><MessageSquare className="w-3 h-3" /> {t.comments.length}</span>}
                  </div>
                  <p className="font-semibold mt-1 truncate">{t.subject}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Créé le {fmt(t.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {creating && <TicketForm isSupport={false} vehicles={vehicles} companies={companies} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
        {detail && <TicketDetail ticket={detail} isSupport={false} vehicles={vehicles} onClose={() => setDetail(null)} onChanged={(t) => { setDetail(t); load(); }} onDelete={null} />}
      </Layout>
    );
  }

  // ---------- Vue SUPPORT / ADMIN : gestion des demandes ----------
  return (
    <Layout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><LifeBuoy className="w-6 h-6 brand-teal" /> Tickets — Support clients</h1>
          <p className="text-sm text-muted-foreground">Traitez les demandes des clients : suivi, priorités, réponses et statuts.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="ticket-add-btn" className="flex items-center gap-1.5 bg-[#F5821F] hover:bg-[#e0741a] text-white font-semibold px-3 py-2 rounded-md text-sm"><Plus className="w-4 h-4" /> Nouveau ticket</button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <div className="glass rounded-lg p-4" data-testid="ticket-stat-total"><p className="label-mono">Total</p><p className="text-2xl font-extrabold data mt-1">{stats.total}</p></div>
          <div className="glass rounded-lg p-4" data-testid="ticket-stat-open"><p className="label-mono">Ouverts / en cours</p><p className="text-2xl font-extrabold data mt-1 brand-orange">{stats.open}</p></div>
          <div className="glass rounded-lg p-4"><p className="label-mono">Urgents</p><p className="text-2xl font-extrabold data mt-1" style={{ color: "#EF4444" }}>{stats.by_priority?.urgente || 0}</p></div>
          <div className="glass rounded-lg p-4"><p className="label-mono">Résolus</p><p className="text-2xl font-extrabold data mt-1" style={{ color: "#10B981" }}>{stats.by_status?.resolu || 0}</p></div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
        <span className="flex items-center gap-1 text-muted-foreground"><Filter className="w-4 h-4" /> Filtres</span>
        <select className={inp} value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="ticket-filter-status"><option value="">Tous statuts</option>{STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}</select>
        <select className={inp} value={fPriority} onChange={(e) => setFPriority(e.target.value)} data-testid="ticket-filter-priority"><option value="">Toutes priorités</option>{PRIORITY.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}</select>
        <input className={inp + " flex-1 min-w-[160px]"} placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ticket-search" />
      </div>

      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50"><tr className="label-mono text-left"><th className="px-3 py-2">Réf.</th><th className="px-3 py-2">Sujet</th><th className="px-3 py-2">Client</th><th className="px-3 py-2">Priorité</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2">Créé</th><th /></tr></thead>
          <tbody className="data">
            {visible.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Aucun ticket</td></tr>}
            {visible.map((t) => (
              <tr key={t.id} className="border-t border-border hover:bg-white/5 cursor-pointer" data-testid={`ticket-row-${t.id}`} onClick={() => setDetail(t)}>
                <td className="px-3 py-2 font-mono text-xs">{t.ref}</td>
                <td className="px-3 py-2 font-semibold max-w-[240px]">
                  <div className="flex items-center gap-2">
                    <span className="truncate">{t.subject}</span>
                    {(t.attachments || []).length > 0 && <span title={`${t.attachments.length} pièce(s) jointe(s)`} className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground shrink-0"><Paperclip className="w-3 h-3" />{t.attachments.length}</span>}
                    {(t.comments || []).length > 0 && (
                      <span title={`${t.comments.length} réponse(s)`} data-testid={`ticket-replies-${t.id}`} className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#17A2B8] bg-[#17A2B8]/15 rounded-full px-1.5 py-0.5 shrink-0">
                        <MessageSquare className="w-3 h-3" /> {t.comments.length}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">{t.client_name || t.company_name || "—"}</td>
                <td className="px-3 py-2"><Badge item={meta(PRIORITY, t.priority)} /></td>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <select className={inp + " py-1"} value={t.status} onChange={(e) => changeStatus(t, e.target.value)} data-testid={`ticket-status-${t.id}`}>
                    {STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 text-xs">{fmt(t.created_at)}</td>
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>{user?.role === "admin" && <button onClick={() => del(t.id)} data-testid={`ticket-del-${t.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <TicketForm isSupport companies={companies} vehicles={vehicles} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {detail && <TicketDetail ticket={detail} isSupport vehicles={vehicles} onClose={() => setDetail(null)} onChanged={(t) => { setDetail(t); load(); }} onDelete={user?.role === "admin" ? () => del(detail.id) : null} />}
    </Layout>
  );
}

function TicketForm({ isSupport, companies, vehicles, onClose, onSaved }) {
  const [f, setF] = useState({ subject: "", client_name: "", company_id: "", vehicle_id: "", category: "support", priority: "normale", description: "" });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} dépasse 10 Mo`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((prev) => [...prev, { path: r.data.path, name: r.data.name, url: r.data.url }]);
      }
    } catch (err) { toast.error("Échec de l'envoi d'une capture"); }
    finally { setUploading(false); }
  };
  const removeAtt = (i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.subject.trim()) return toast.error("Le sujet est requis");
    setBusy(true);
    try { await api.post("/tickets", { ...f, attachments }); toast.success(isSupport ? "Ticket créé" : "Demande envoyée au support ✅"); onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="glass rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-6 animate-slidein" onClick={(e) => e.stopPropagation()} data-testid="ticket-form">
        <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold">{isSupport ? "Nouveau ticket" : "Créer un ticket au support Boomerang"}</h2><button onClick={onClose}><X className="w-5 h-5 text-muted-foreground" /></button></div>
        <form onSubmit={submit} className="space-y-3">
          <input className={inp + " w-full"} placeholder="Objet de la demande *" value={f.subject} onChange={up("subject")} data-testid="ticket-subject" />
          {isSupport ? (
            <div className="grid grid-cols-2 gap-3">
              <input className={inp} placeholder="Nom du client" value={f.client_name} onChange={up("client_name")} data-testid="ticket-client" />
              <select className={inp} value={f.company_id} onChange={up("company_id")} data-testid="ticket-company"><option value="">Compagnie…</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
              <select className={inp} value={f.category} onChange={up("category")} data-testid="ticket-category">{CATEGORY.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}</select>
              <select className={inp} value={f.priority} onChange={up("priority")} data-testid="ticket-priority">{PRIORITY.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}</select>
              <select className={inp + " col-span-2"} value={f.vehicle_id} onChange={up("vehicle_id")} data-testid="ticket-vehicle"><option value="">Véhicule concerné (optionnel)…</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.plate}</option>)}</select>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2 text-xs text-muted-foreground">Priorité</label>
              <select className={inp + " col-span-2"} value={f.priority} onChange={up("priority")} data-testid="ticket-priority">{PRIORITY.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}</select>
              <select className={inp + " col-span-2"} value={f.vehicle_id} onChange={up("vehicle_id")} data-testid="ticket-vehicle"><option value="">Véhicule concerné (optionnel)…</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.plate}</option>)}</select>
            </div>
          )}
          <textarea className={inp + " w-full min-h-[100px]"} placeholder="Décrivez votre demande le plus précisément possible…" value={f.description} onChange={up("description")} data-testid="ticket-description" />

          <div>
            <label className="flex items-center gap-2 cursor-pointer w-fit text-sm font-medium text-[#17A2B8] hover:text-[#1391a5]" data-testid="ticket-attach-label">
              <ImagePlus className="w-4 h-4" /> Ajouter des captures d'écran
              <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="ticket-attach-input" />
            </label>
            {uploading && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Loader2 className="w-3 h-3 animate-spin" /> Envoi en cours…</p>}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="ticket-attach-preview">
                {attachments.map((a, i) => (
                  <div key={i} className="relative group border border-border rounded-md overflow-hidden">
                    <img src={fileUrl(a)} alt={a.name} className="h-20 w-28 object-cover" />
                    <button type="button" onClick={() => removeAtt(i)} data-testid={`ticket-attach-remove-${i}`} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button type="submit" disabled={busy || uploading} data-testid="ticket-submit" className="w-full bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold py-2.5 rounded-md disabled:opacity-50">{isSupport ? "Créer le ticket" : "Envoyer au support"}</button>
        </form>
      </div>
    </div>
  );
}

function TicketDetail({ ticket, isSupport, vehicles, onClose, onChanged, onDelete }) {
  const [t, setT] = useState(ticket);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const veh = vehicles.find((v) => v.id === t.vehicle_id);
  const save = async (patch) => { const r = await api.put(`/tickets/${t.id}`, { ...toPayload(t), ...patch }); setT(r.data); onChanged(r.data); };
  const addComment = async () => {
    if (!comment.trim() || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/tickets/${t.id}/comment`, { text: comment });
      setComment(""); setT(r.data); onChanged(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="glass rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 animate-slidein" onClick={(e) => e.stopPropagation()} data-testid="ticket-detail">
        <div className="flex items-start justify-between mb-2">
          <div><p className="label-mono">{t.ref}</p><h2 className="text-lg font-bold">{t.subject}</h2></div>
          <button onClick={onClose}><X className="w-5 h-5 text-muted-foreground" /></button>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {isSupport ? (
            <>
              <select className={inp + " py-1"} value={t.priority} onChange={(e) => save({ priority: e.target.value })} data-testid="detail-priority">{PRIORITY.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}</select>
              <select className={inp + " py-1"} value={t.status} onChange={(e) => save({ status: e.target.value })} data-testid="detail-status">{STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}</select>
              <Badge item={meta(CATEGORY, t.category)} />
            </>
          ) : (
            <>
              <Badge item={meta(PRIORITY, t.priority)} />
              <Badge item={meta(STATUS, t.status)} />
            </>
          )}
        </div>
        {isSupport && (
          <div className="grid grid-cols-2 gap-2 data text-xs mb-3 text-muted-foreground">
            <p><span className="label-mono">Client</span><br />{t.client_name || "—"}</p>
            <p><span className="label-mono">Compagnie</span><br />{t.company_name || "—"}</p>
            <p><span className="label-mono">Véhicule</span><br />{veh?.name || "—"}</p>
            <p><span className="label-mono">Créé</span><br />{fmt(t.created_at)}</p>
          </div>
        )}
        {!isSupport && <p className="text-xs text-muted-foreground mb-3">Créé le {fmt(t.created_at)}{veh ? ` · ${veh.name}` : ""}</p>}
        {t.description && <p className="text-sm bg-secondary/40 rounded-md p-3 mb-2 whitespace-pre-wrap">{t.description}</p>}
        <Attachments list={t.attachments} />

        <div className="my-3">
          <p className="label-mono flex items-center gap-1 mb-1.5"><MessageSquare className="w-3.5 h-3.5" /> {isSupport ? "Suivi" : "Échanges avec le support"} ({(t.comments || []).length})</p>
          <div className="space-y-1.5 max-h-52 overflow-y-auto" data-testid="ticket-comments">
            {(t.comments || []).map((c) => (
              <div key={c.id} className={`text-xs rounded-md p-2 ${c.is_support ? "bg-[#17A2B8]/12 border border-[#17A2B8]/30" : "bg-secondary/40"}`}>
                <span className="font-semibold">{c.is_support ? "Support Boomerang" : c.author}</span> <span className="text-muted-foreground">· {fmt(c.at)}</span>
                <p className="mt-0.5 whitespace-pre-wrap">{c.text}</p>
              </div>
            ))}
            {(t.comments || []).length === 0 && <p className="text-xs text-muted-foreground">{isSupport ? "Aucun commentaire." : "Notre équipe vous répondra ici."}</p>}
          </div>
          <div className="flex gap-2 mt-2">
            <input className={inp + " flex-1"} placeholder={isSupport ? "Répondre au client…" : "Écrire un message…"} value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} data-testid="ticket-comment-input" />
            <button onClick={addComment} disabled={sending} data-testid="ticket-comment-send" className="bg-[#17A2B8] text-white px-3 rounded-md disabled:opacity-50">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
          </div>
        </div>
        {onDelete && <button onClick={onDelete} data-testid="detail-delete" className="text-sm text-destructive hover:underline flex items-center gap-1"><Trash2 className="w-4 h-4" /> Supprimer ce ticket</button>}
      </div>
    </div>
  );
}
